import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@colorglo/ui/components";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { api } from "../api.ts";
import { useT } from "../lib/locale.ts";

export interface ResetSearch {
  readonly token?: string;
}

export const Route = createFileRoute("/aterstall-losenord")({
  validateSearch: (search: Record<string, unknown>): ResetSearch =>
    typeof search.token === "string" ? { token: search.token } : {},
  component: ResetPassword,
});

function ResetPassword() {
  const t = useT();
  const { token } = Route.useSearch();

  const passwordId = useId();
  const hintId = useId();
  const errorId = useId();

  const [password, setPassword] = useState("");

  const reset = useMutation({
    mutationFn: async () => {
      const { error } = await api.v1["password-resets"].accept.post({
        token: token as string,
        password,
      });
      if (error !== null) throw error;
    },
  });

  if (token === undefined) {
    return (
      <div className="mx-auto max-w-md p-6">
        <p role="alert" className="text-body">
          {t("reset.missing")}
        </p>
      </div>
    );
  }

  if (reset.isSuccess) {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-4 p-6">
        <h1 className="text-h1">{t("reset.done")}</h1>
        <Link to="/logga-in" className="text-body text-primary underline underline-offset-4">
          {t("auth.sign_in.submit")}
        </Link>
      </div>
    );
  }

  const expired =
    reset.error !== null && [401, 410].includes((reset.error as { status?: number }).status ?? 0);

  return (
    <form
      className="mx-auto max-w-md p-6"
      onSubmit={(event) => {
        event.preventDefault();
        reset.mutate();
      }}
    >
      <FieldGroup>
        <div className="flex flex-col gap-1">
          <h1 className="text-h1">{t("reset.title")}</h1>
          <p className="text-body-sm text-muted-foreground">{t("reset.body")}</p>
        </div>

        <Field>
          <FieldLabel htmlFor={passwordId}>{t("reset.password")}</FieldLabel>
          <Input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            aria-describedby={reset.isError ? `${hintId} ${errorId}` : hintId}
            aria-invalid={reset.isError || undefined}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <FieldDescription id={hintId}>{t("reset.password_hint")}</FieldDescription>
          {reset.isError && (
            <FieldError id={errorId}>
              {expired ? t("reset.failed") : t("error.validation_failed")}
            </FieldError>
          )}
        </Field>

        <Field>
          <Button type="submit" disabled={reset.isPending}>
            {t("reset.submit")}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
